import { useEffect, useMemo, useState } from 'react';
import { euchreBestSuit, euchrePartnerOf, euchreTeamOf, euchreTrumpPower, sortHand, type EuchreBidMark, type EuchreLegal, type EuchreView } from '@cardhub/engine';
import { PlayingCard, shortCard } from '../cards/PlayingCard';
import { Hand } from '../cards/Hand';
import { Modal } from '../components/ui';
import { useT } from '../i18n';
import { useGame } from '../store/game';
import { seatPosition, SeatView, MySeat, useNarrow, useSpeech } from './common';

const OFFSETS: Record<string, { x: number; y: number }> = {
  bottom: { x: 0, y: 34 },
  left: { x: -62, y: 0 },
  top: { x: 0, y: -34 },
  right: { x: 62, y: 0 },
};

const SUIT_SYMBOL: Record<string, string> = { S: '♠', H: '♥', D: '♦', C: '♣' };
const SUIT_NAME: Record<string, string> = { S: 'Spades', H: 'Hearts', D: 'Diamonds', C: 'Clubs' };
const isRedSuit = (s: string) => s === 'H' || s === 'D';
type Suit = 'S' | 'H' | 'D' | 'C';

export function EuchreTable({ scoresOpen, onCloseScores }: { scoresOpen: boolean; onCloseScores: () => void }) {
  const t = useT();
  const view = useGame((s) => s.view) as EuchreView;
  const legal = useGame((s) => s.legal) as EuchreLegal | null;
  const seats = useGame((s) => s.seats);
  const mySeat = useGame((s) => s.mySeat);
  const turn = useGame((s) => s.turn);
  const act = useGame((s) => s.act);
  const speech = useSpeech();
  const [selected, setSelected] = useState<string | null>(null);
  const hand = useMemo(() => sortHand(view.hand), [view.hand]);
  const playable = new Set(legal?.kind === 'play' ? legal.cards : []);
  const name = (s: number) => seats[s]?.user?.username ?? `Seat ${s + 1}`;
  const teamName = (team: number) => `${name(team)} & ${name(team + 2)}`;
  const myTeam = mySeat !== null ? euchreTeamOf(mySeat) : 0;
  const myTurn = mySeat !== null && turn?.seats.includes(mySeat);
  const narrow = useNarrow();
  const trickSize = narrow ? 'md' : 'lg';
  const k = narrow ? 0.7 : 1;
  const bidding = view.phase === 'BID1' || view.phase === 'BID2';
  const playing = view.phase === 'PLAYING' || view.phase === 'TRICK_END';
  const iDiscard = Boolean(myTurn && legal?.kind === 'discard');
  const teamTricks = (team: number) => (view.tricksWon[team] ?? 0) + (view.tricksWon[team + 2] ?? 0);

  // A fresh deal (or phase) always starts with nothing selected.
  useEffect(() => {
    setSelected(null);
  }, [view.round, view.phase]);

  const markLabel = (m: EuchreBidMark) => (m === null ? null : m === 'pass' ? t('ta.pass') : m === 'order' ? t('eu.orderedUp') : `${t('eu.called')} ${SUIT_SYMBOL[m]}`);

  const onCard = (c: string) => {
    if (!myTurn) return;
    if (legal?.kind === 'discard') {
      setSelected(selected === c ? null : c);
      return;
    }
    if (legal?.kind !== 'play' || !playable.has(c)) return;
    setSelected(c);
    void act({ type: 'play', card: c }).finally(() => setSelected(null));
  };

  const teamPill = (team: number) => (
    <span className={`pill team-pill ${team === myTeam ? 'mine' : ''}`} key={team}>
      {team === myTeam ? t('sp.us') : t('sp.them')}: {view.teamScores[team] ?? 0}
      {playing && ` · ${teamTricks(team)}`}
    </span>
  );

  return (
    <>
      <div className="stage">
        <div className="felt" />
        {[0, 1, 2, 3].map((s) => (
          <SeatView
            key={s}
            seat={s}
            info={seats[s]}
            mySeat={mySeat}
            total={4}
            turn={turn}
            speech={speech[s]}
            folded={view.sittingOut === s}
            sub={<span className="dim num">{view.teamScores[euchreTeamOf(s)] ?? 0} pts</span>}
            tags={
              <>
                {view.dealer === s && <span className="tag">D</span>}
                {mySeat !== null && s === euchrePartnerOf(mySeat) && <span className="tag">{t('sp.partner')}</span>}
                {bidding && markLabel(view.bids[s] ?? null) && <span className="tag">{markLabel(view.bids[s] ?? null)}</span>}
                {!bidding && view.maker === s && <span className="tag seen">{view.alone ? t('eu.alone') : t('eu.maker')}</span>}
                {view.sittingOut === s && <span className="tag">{t('eu.sitting')}</span>}
                {playing && view.sittingOut !== s && <span className="tag">{view.tricksWon[s] ?? 0}</span>}
              </>
            }
          />
        ))}
        <div className="felt-center centered">
          <div className="center-note">
            {t('sp.round', { n: view.round })}
            {view.trump && (
              <>
                {' · '}
                {t('ta.trump')} <span style={{ color: isRedSuit(view.trump) ? '#ff8a8a' : undefined, fontSize: '1.2em' }}>{SUIT_SYMBOL[view.trump]}</span>
              </>
            )}
            {bidding && !myTurn && turn?.seats[0] !== undefined && ` · ${t('table.waitingFor', { name: name(turn.seats[0]) })}`}
            {view.phase === 'DISCARD' && !iDiscard && turn?.seats[0] !== undefined && ` · ${t('eu.waitingDiscard', { name: name(turn.seats[0]) })}`}
          </div>
          {(bidding || view.phase === 'DISCARD') && (
            <div className="col" style={{ alignItems: 'center', gap: 4 }} aria-label={t('eu.turnedUp')}>
              {view.turnCard ? <PlayingCard id={view.turnCard} size="md" /> : <PlayingCard faceDown size="md" />}
              <span className="center-note" style={{ fontSize: 'var(--fs-xs)' }}>
                {view.turnCard ? t('eu.turnedUp') : t('eu.turnedDown', { suit: SUIT_SYMBOL[view.turnSuit]! })}
              </span>
            </div>
          )}
          <div className="row" style={{ justifyContent: 'center', gap: 6 }} aria-label={t('table.scores')}>
            {teamPill(myTeam)}
            {teamPill(1 - myTeam)}
          </div>
          <div className={`trick ${view.phase === 'TRICK_END' ? 'collect' : ''}`} key={`trick-${view.round}-${view.tricks.length}-${view.phase === 'TRICK_END'}`} aria-label="Cards on the table">
            {view.trick.map((p, i) => {
              const dir = seatPosition(p.seat, mySeat, 4).dir;
              const o = OFFSETS[dir]!;
              const off = { x: o.x * k, y: o.y * k };
              const w = view.phase === 'TRICK_END' && view.lastTrick ? OFFSETS[seatPosition(view.lastTrick.winner, mySeat, 4).dir]! : null;
              const winnerDir = w ? { x: w.x * k, y: w.y * k } : null;
              return (
                <div
                  key={p.card}
                  className="slot fly"
                  style={{
                    transform: `translate(calc(-50% + ${off.x}px), calc(-50% + ${off.y}px)) rotate(${(i - 1.5) * 4}deg)`,
                    ['--from-x' as string]: `${off.x * 3}px`,
                    ['--from-y' as string]: `${off.y * 4}px`,
                    ...(winnerDir ? { ['--to-x' as string]: `${winnerDir.x * 3}px`, ['--to-y' as string]: `${winnerDir.y * 4}px`, animationDelay: '0.45s' } : {}),
                    zIndex: i,
                  }}
                >
                  <PlayingCard id={p.card} size={trickSize} />
                </div>
              );
            })}
          </div>
          {view.phase === 'ROUND_END' && view.roundResults.length > 0 && (
            <div className="card glass" style={{ textAlign: 'center' }}>
              <strong>{t('sp.roundScores', { n: view.round })}</strong>
              <div className="row" style={{ justifyContent: 'center', marginTop: 6 }}>
                {view.roundResults.at(-1)!.map((r, team) => (
                  <span key={team} className={`pill ${r.delta > 0 ? 'accent' : r.euchred ? 'danger' : ''}`}>
                    {team === myTeam ? t('sp.us') : t('sp.them')}: +{r.delta}
                    {r.euchred && ` · ${t('eu.euchred')}`}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
        <MySeat seat={mySeat !== null ? seats[mySeat] : undefined} sub={mySeat !== null ? <>{view.teamScores[myTeam] ?? 0} pts{bidding && markLabel(view.bids[mySeat] ?? null) ? ` · ${markLabel(view.bids[mySeat] ?? null)}` : ''}</> : null} />
      </div>
      <div className="table-bottom">
        {myTurn && legal?.kind === 'bid' && (
          <BidPanel
            legal={legal}
            suggested={euchreBestSuit(view.hand, view.turnSuit)}
            onOrder={(alone) => void act({ type: 'orderUp', alone })}
            onCall={(suit, alone) => void act({ type: 'call', suit, alone })}
            onPass={() => void act({ type: 'pass' })}
          />
        )}
        {iDiscard && (
          <div className="bid-picker" role="group" aria-label={t('eu.discardTitle')}>
            <div className="bid-head">
              <strong>{t('eu.discardTitle')}</strong>
              <span className="dim">{t('eu.pickUp', { card: view.turnCard ? shortCard(view.turnCard) : '' })}</span>
            </div>
            <button className="btn primary block bid-confirm" disabled={!selected} onClick={() => selected && void act({ type: 'discard', card: selected })}>
              {selected ? t('eu.discardCard', { card: shortCard(selected) }) : t('eu.discard')}
            </button>
          </div>
        )}
        <Hand size="lg" label="Your hand">
          {hand.map((c, i) => (
            <PlayingCard
              key={c}
              id={c}
              size="lg"
              dealIndex={bidding ? i : undefined}
              wild={view.trump !== null && euchreTrumpPower(c, view.trump) !== null && (euchreTrumpPower(c, view.trump) ?? 0) >= 29}
              selected={selected === c}
              playable={myTurn && (iDiscard || playable.has(c))}
              unplayable={myTurn && legal?.kind === 'play' && !playable.has(c)}
              onClick={() => onCard(c)}
            />
          ))}
        </Hand>
      </div>
      <Modal open={scoresOpen} onClose={onCloseScores} title={t('table.scores')}>
        <table className="scoreboard">
          <thead>
            <tr>
              <th>#</th>
              {[0, 1].map((team) => (
                <th key={team} className="ellipsis" style={{ maxWidth: 110 }}>{teamName(team)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {view.roundResults.map((row, r) => (
              <tr key={r}>
                <td>
                  R{r + 1}
                  <div className="dim" style={{ fontSize: 'var(--fs-xs)', color: isRedSuit(row[0]!.trump) ? 'var(--danger)' : undefined }}>{SUIT_SYMBOL[row[0]!.trump]}</div>
                </td>
                {row.map((x, team) => (
                  <td key={team} className="num">
                    {x.delta > 0 ? '+' : ''}{x.delta}
                    <div className="dim" style={{ fontSize: 'var(--fs-xs)' }}>
                      {x.maker ? `★ ${x.tricks}` : x.tricks}
                      {x.alone ? ` · ${t('eu.alone')}` : ''}
                      {x.euchred ? ` · ${t('eu.euchred')}` : ''}
                    </div>
                  </td>
                ))}
              </tr>
            ))}
            <tr>
              <td><strong>Total</strong></td>
              {view.teamScores.map((v, team) => (
                <td key={team} className="num"><strong>{v}</strong></td>
              ))}
            </tr>
          </tbody>
        </table>
        <p className="dim" style={{ fontSize: 'var(--fs-xs)' }}>{t('eu.scoreHint', { target: view.config.targetScore })}</p>
      </Modal>
    </>
  );
}

/**
 * Auction panel. Round 1: order up the turned card's suit or pass. Round 2: pick any other suit or pass (the dealer
 * must call when everyone else passed). A separate toggle sends the call as "going alone".
 */
function BidPanel({
  legal,
  suggested,
  onOrder,
  onCall,
  onPass,
}: {
  legal: Extract<EuchreLegal, { kind: 'bid' }>;
  suggested: Suit;
  onOrder: (alone: boolean) => void;
  onCall: (suit: Suit, alone: boolean) => void;
  onPass: () => void;
}) {
  const t = useT();
  const [alone, setAlone] = useState(false);
  const [suit, setSuit] = useState<Suit>(legal.suits.includes(suggested) ? suggested : legal.suits[0]!);
  const [sent, setSent] = useState(false);
  const send = (fn: () => void) => {
    setSent(true);
    fn();
    setTimeout(() => setSent(false), 1500);
  };
  const round1 = legal.round === 1;
  return (
    <div className="bid-picker" role="group" aria-label={t('eu.bid')}>
      <div className="bid-head">
        <strong>{t('eu.bid')}</strong>
        <span className="dim">
          {round1 ? t('eu.round1Hint', { suit: SUIT_SYMBOL[legal.suits[0]!]! }) : legal.canPass ? t('eu.round2Hint') : t('eu.mustCall')}
        </span>
      </div>
      {!round1 && (
        <div className="bid-chips" role="radiogroup" aria-label={t('eu.call')}>
          {legal.suits.map((s) => (
            <button
              key={s}
              role="radio"
              aria-checked={suit === s}
              className={`bid-chip ${suit === s ? 'on' : ''} ${s === suggested ? 'star' : ''}`}
              style={{ color: isRedSuit(s) ? 'var(--danger)' : undefined, fontSize: '1.6em' }}
              aria-label={SUIT_NAME[s]}
              onClick={() => setSuit(s as Suit)}
            >
              {SUIT_SYMBOL[s]}
            </button>
          ))}
        </div>
      )}
      {legal.canAlone && (
        <button className={`bid-chip ${alone ? 'on' : ''}`} role="switch" aria-checked={alone} onClick={() => setAlone(!alone)} style={{ alignSelf: 'center', padding: '0 16px', width: 'auto', maxWidth: 'none', aspectRatio: 'auto', height: 34, borderRadius: 999, whiteSpace: 'nowrap' }}>
          {t('eu.alone')}
        </button>
      )}
      <div className="row" style={{ gap: 8 }}>
        {legal.canPass && (
          <button className="btn grow" disabled={sent} onClick={() => send(onPass)}>
            {t('ta.pass')}
          </button>
        )}
        <button className="btn primary grow bid-confirm" disabled={sent} onClick={() => send(() => (round1 ? onOrder(alone) : onCall(suit, alone)))}>
          {round1 ? t('eu.orderUp') : t('eu.callSuit', { suit: SUIT_SYMBOL[suit]! })}
          {alone ? ` · ${t('eu.alone')}` : ''}
        </button>
      </div>
    </div>
  );
}
